import type { LocalPresetRecord } from "../../shared/types.js";

const pendingDeletes = new Set<string>();

export function markPendingLibraryDeletes(ids: string[]): void {
  for (const id of ids) pendingDeletes.add(id);
}

export function clearPendingLibraryDeletes(ids: string[]): void {
  for (const id of ids) pendingDeletes.delete(id);
}

export function isPendingLibraryDelete(id: string): boolean {
  return pendingDeletes.has(id);
}

export function mergeLibrarySnapshots(
  current: LocalPresetRecord[],
  restored: LocalPresetRecord[],
): LocalPresetRecord[] {
  const merged = [...current];
  for (const record of restored) {
    if (!merged.some((preset) => preset.id === record.id)) {
      merged.push(record);
    }
  }
  return merged.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export type DeleteLibraryPresetResult =
  | { ok: true }
  | { ok: false; status: number; networkError?: boolean };

/** Delete a preset on the server; 404 is treated as already removed. */
export async function deleteLibraryPresetOnServer(
  id: string,
): Promise<DeleteLibraryPresetResult> {
  try {
    const res = await fetch(`/api/presets/${encodeURIComponent(id)}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (res.status === 204 || res.status === 404) {
      return { ok: true };
    }
    return { ok: false, status: res.status };
  } catch {
    return { ok: false, status: 0, networkError: true };
  }
}

export function formatLibraryDeleteRevertError(failed: LocalPresetRecord[]): string {
  if (failed.length === 1) {
    const name = failed[0]?.name ?? "preset";
    return `Could not delete “${name}”. It was restored to your library.`;
  }
  return `Could not delete ${failed.length} presets. They were restored to your library.`;
}
